import { Link } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { PublicNav } from '../components/layout/PublicNav';

const EFFECTIVE = '27 June 2026';
const CONTACT = 'hello@urava.gt.tc';

type Section = { heading: string; body: React.ReactNode };

const SECTIONS: Section[] = [
  {
    heading: '1. Who we are',
    body: (
      <p>
        Urava ("Urava", "we", "us") is an AI legal-research service for Indian law,
        available at <span className="text-accent">urava.app</span>, on WhatsApp, and as an
        Android app that wraps the same website. This policy explains what data we collect,
        why, and the choices you have.
      </p>
    ),
  },
  {
    heading: '2. Information we collect',
    body: (
      <ul className="list-disc pl-5 space-y-2">
        <li>
          <span className="text-white font-medium">Account details</span> — your name and
          email address, provided directly or via Google Sign-In, and (optionally) your firm
          name and phone number.
        </li>
        <li>
          <span className="text-white font-medium">Your research content</span> — the
          questions you ask and any documents you upload for analysis.
        </li>
        <li>
          <span className="text-white font-medium">Usage &amp; device data</span> — pages
          viewed, clicks, approximate region (derived from IP), browser, OS and device type,
          collected through our own analytics to improve the product.
        </li>
        <li>
          <span className="text-white font-medium">Payment data</span> — when you subscribe
          on the web, payments are processed by Razorpay. We receive a confirmation and your
          plan; we do not store your card or bank details.
        </li>
      </ul>
    ),
  },
  {
    heading: '3. How we use your information',
    body: (
      <ul className="list-disc pl-5 space-y-2">
        <li>To generate your legal-research memoranda and run the service.</li>
        <li>To authenticate you and enforce plan limits.</li>
        <li>To improve reliability, performance and features.</li>
        <li>To communicate service notices and respond to support requests.</li>
      </ul>
    ),
  },
  {
    heading: '4. Service providers',
    body: (
      <p>
        To deliver the service we share the minimum necessary data with trusted processors:
        document understanding and Indian-language translation (Sarvam AI), legal memo
        generation (DeepSeek), real-time legal-source search (Tavily), Google Sign-In
        (Google), and payments (Razorpay). They process data only to perform their function.
        We do <span className="text-white font-medium">not</span> sell your personal data,
        and we do <span className="text-white font-medium">not</span> use your queries or
        documents to train AI models.
      </p>
    ),
  },
  {
    heading: '5. Payments in the Android app',
    body: (
      <p>
        The Android app does not sell subscriptions in-app. To upgrade a paid plan you open{' '}
        <span className="text-accent">urava.app</span> in your browser, where Razorpay handles
        the payment. Your upgraded plan then unlocks automatically in the app.
      </p>
    ),
  },
  {
    heading: '6. Data retention & security',
    body: (
      <p>
        Your account, research history and uploaded content are retained until you delete them
        or close your account. Data is encrypted in transit and at rest. No system is perfectly
        secure, but we take reasonable technical and organisational measures to protect your
        information.
      </p>
    ),
  },
  {
    heading: '7. Your choices & rights',
    body: (
      <p>
        You can view, edit and delete your research and documents from within the app, and you
        can request deletion of your account and associated data by writing to us. You can opt
        out of analytics at any time. To exercise any right, contact us at{' '}
        <a href={`mailto:${CONTACT}`} className="text-accent hover:underline">{CONTACT}</a>.
      </p>
    ),
  },
  {
    heading: '8. Children',
    body: (
      <p>
        Urava is intended for legal professionals and adult users and is not directed to
        children under 18.
      </p>
    ),
  },
  {
    heading: '9. Changes to this policy',
    body: (
      <p>
        We may update this policy from time to time. Material changes will be reflected by an
        updated effective date on this page.
      </p>
    ),
  },
  {
    heading: '10. Contact',
    body: (
      <p>
        Questions about this policy or your data? Email{' '}
        <a href={`mailto:${CONTACT}`} className="text-accent hover:underline">{CONTACT}</a>.
      </p>
    ),
  },
];

export function PrivacyPage() {
  return (
    <div className="min-h-screen bg-legal-900 overflow-x-hidden">
      <PublicNav />

      <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8 max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-accent/10 border border-accent/20 text-accent text-sm font-medium px-4 py-1.5 rounded-full mb-6">
            <ShieldCheck className="h-4 w-4" />
            <span>Privacy</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-3">Privacy Policy</h1>
          <p className="text-legal-400 text-sm">Effective {EFFECTIVE}</p>
        </div>

        <div className="space-y-8">
          {SECTIONS.map((s) => (
            <section key={s.heading}>
              <h2 className="text-white font-semibold text-lg mb-2">{s.heading}</h2>
              <div className="text-legal-300 text-sm leading-relaxed">{s.body}</div>
            </section>
          ))}
        </div>

        <div className="mt-14 text-center">
          <Link to="/" className="text-accent text-sm hover:underline">← Back to Urava</Link>
        </div>
      </div>
    </div>
  );
}
